import { sfx } from "../sfx/SfxEngine";

export type ArcadeGameId =
  | "note-invaders"
  | "chord-tower"
  | "scale-racer"
  | "flappy-note"
  | "rhythm-simon"
  | "tempo-runner"
  | "dual-hands";

export type ArcadeMeta = {
  id: ArcadeGameId;
  title: string;
  blurb: string;
  /** All season-1 cabinets are endless survival / high-score runs. */
  endless: true;
  help: string;
};

export type ArcadeHostHooks = {
  playTone: (midi: number, velocity?: number) => void;
  onGameOver: (score: number, detail: string) => void;
  setHud: (score: number, extra?: string) => void;
  setIgnoreApiNotes: (on: boolean) => void;
  /** Cut sustained piano notes (Simon demo / game over). */
  silence: () => void;
};

export interface ArcadeGame {
  readonly meta: ArcadeMeta;
  start(hooks: ArcadeHostHooks, w: number, h: number): void;
  stop(): void;
  resize(w: number, h: number): void;
  tick(dt: number): void;
  draw(ctx: CanvasRenderingContext2D): void;
  onNote(midi: number, velocity: number, source: string): void;
}

const C_MAJOR = [60, 62, 64, 65, 67, 69, 71, 72]; // C4–C5
const WHITE_TRIADS: number[][] = [
  [60, 64, 67], // C E G
  [62, 65, 69], // D F A
  [64, 67, 71], // E G B
  [65, 69, 72], // F A C
];

const NOTE_NAME = (m: number): string => {
  const names = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
  return names[((m % 12) + 12) % 12] + (Math.floor(m / 12) - 1);
};

/** Same note or same pitch-class within ±1 octave (QWERTY / MIDI friendly). */
function pitchMatch(played: number, target: number): boolean {
  if (played === target) return true;
  return played % 12 === target % 12 && Math.abs(played - target) <= 12;
}

function findMatchIndex(list: number[], played: number): number {
  return list.findIndex((t) => pitchMatch(played, t));
}

function loadHi(id: string): number {
  try {
    const raw = localStorage.getItem("pianoTutorArcadeScores");
    if (!raw) return 0;
    const j = JSON.parse(raw) as Record<string, number>;
    return j[id] ?? 0;
  } catch {
    return 0;
  }
}

export function saveHi(id: string, score: number): number {
  const prev = loadHi(id);
  const next = Math.max(prev, score);
  try {
    const raw = localStorage.getItem("pianoTutorArcadeScores");
    const j = raw ? (JSON.parse(raw) as Record<string, number>) : {};
    j[id] = next;
    localStorage.setItem("pianoTutorArcadeScores", JSON.stringify(j));
  } catch {
    /* ignore */
  }
  return next;
}

export function getHi(id: string): number {
  return loadHi(id);
}

export const ARCADE_CATALOG: ArcadeMeta[] = [
  {
    id: "note-invaders",
    title: "Note Invaders",
    blurb: "Blast falling note asteroids with perfect pitch lasers.",
    endless: true,
    help: "Play the falling note before it hits the bottom. 3 lives · speed ramps forever.",
  },
  {
    id: "chord-tower",
    title: "Chord Tower",
    blurb: "Stack floors by locking chords on time.",
    endless: true,
    help: "Play all notes of the shown chord together. Timer shrinks each floor.",
  },
  {
    id: "scale-racer",
    title: "Scale Racer",
    blurb: "Accelerate with ascending / descending scales.",
    endless: true,
    help: "Hit the next C-major note before the timer empties. Wrong note or timeout = −1 life. Window shrinks as you climb.",
  },
  {
    id: "flappy-note",
    title: "Flappy Note",
    blurb: "Steer height with pitch register.",
    endless: true,
    help: "Low keys = low flight, high keys = climb. Touch a pipe = wipeout. Scroll speed ramps forever.",
  },
  {
    id: "rhythm-simon",
    title: "Rhythm Simon",
    blurb: "Repeat expanding musical memory patterns.",
    endless: true,
    help: "Listen, then replay the sequence. Each clear adds one more note.",
  },
  {
    id: "tempo-runner",
    title: "Tempo Runner",
    blurb: "Clear beat gates with the matching note.",
    endless: true,
    help: "Each approaching gate shows a note — play that exact note in the hit window. Wrong note or miss = crash.",
  },
  {
    id: "dual-hands",
    title: "Dual Hands",
    blurb: "Hit LH bass + RH treble together before the timer dies.",
    endless: true,
    help: "Each round shows a LEFT (bass) and RIGHT (treble) note — play both (any order) before the bar empties. Window shrinks.",
  },
];

abstract class BaseGame implements ArcadeGame {
  abstract readonly meta: ArcadeMeta;
  protected hooks!: ArcadeHostHooks;
  protected w = 640;
  protected h = 360;
  protected score = 0;
  protected alive = false;
  protected ended = false;

  start(hooks: ArcadeHostHooks, w: number, h: number): void {
    this.hooks = hooks;
    this.w = w;
    this.h = h;
    this.score = 0;
    this.alive = true;
    this.ended = false;
    this.onStart();
    this.hooks.setHud(0);
  }

  stop(): void {
    this.alive = false;
    this.onStop();
  }

  resize(w: number, h: number): void {
    this.w = w;
    this.h = h;
  }

  protected bump(n: number): void {
    this.score += n;
    this.hooks.setHud(this.score);
  }

  protected gameOver(detail: string): void {
    if (this.ended) return;
    this.ended = true;
    this.alive = false;
    this.hooks.silence();
    saveHi(this.meta.id, this.score);
    sfx.play("miss");
    this.hooks.onGameOver(this.score, detail);
  }

  abstract onStart(): void;
  onStop(): void {}
  abstract tick(dt: number): void;
  abstract draw(ctx: CanvasRenderingContext2D): void;
  abstract onNote(midi: number, velocity: number, source: string): void;
}

/** 1 — Note Invaders */
class NoteInvaders extends BaseGame {
  readonly meta = ARCADE_CATALOG[0]!;
  private invaders: Array<{ midi: number; x: number; y: number; vy: number }> = [];
  private spawn = 0;
  private lives = 3;
  private difficulty = 1;

  onStart(): void {
    this.invaders = [];
    this.spawn = 0.4;
    this.lives = 3;
    this.difficulty = 1;
  }

  tick(dt: number): void {
    if (!this.alive) return;
    this.spawn -= dt;
    this.difficulty += dt * 0.04;
    if (this.spawn <= 0) {
      this.spawn = Math.max(0.35, 1.35 - this.difficulty * 0.08);
      const midi = C_MAJOR[Math.floor(Math.random() * C_MAJOR.length)]!;
      this.invaders.push({
        midi,
        x: 40 + Math.random() * (this.w - 80),
        y: -30,
        vy: 40 + this.difficulty * 18 + Math.random() * 30,
      });
    }
    for (const inv of this.invaders) inv.y += inv.vy * dt;
    const hitBottom = this.invaders.filter((i) => i.y > this.h - 28);
    if (hitBottom.length) {
      this.invaders = this.invaders.filter((i) => i.y <= this.h - 28);
      this.lives -= hitBottom.length;
      sfx.play("miss");
      if (this.lives <= 0) this.gameOver("Asteroids breached the surface");
      else this.hooks.setHud(this.score, `Lives ${this.lives}`);
    }
  }

  onNote(midi: number): void {
    if (!this.alive) return;
    const idx = this.invaders.findIndex((i) => pitchMatch(midi, i.midi));
    if (idx >= 0) {
      this.invaders.splice(idx, 1);
      this.bump(100 + Math.floor(this.difficulty * 20));
      sfx.play("hit");
      this.hooks.setHud(this.score, `Lives ${this.lives}`);
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = "#061018";
    ctx.fillRect(0, 0, this.w, this.h);
    ctx.strokeStyle = "rgba(47,230,176,0.2)";
    ctx.beginPath();
    ctx.moveTo(0, this.h - 24);
    ctx.lineTo(this.w, this.h - 24);
    ctx.stroke();
    for (const inv of this.invaders) {
      ctx.fillStyle = "#2fe6b0";
      ctx.beginPath();
      ctx.arc(inv.x, inv.y, 18, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#04140f";
      ctx.font = "700 12px Oxanium, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(NOTE_NAME(inv.midi), inv.x, inv.y);
    }
    ctx.fillStyle = "#f0c15a";
    ctx.font = "700 14px Oxanium, sans-serif";
    ctx.textAlign = "left";
    ctx.fillText(`LIVES ${Math.max(0, this.lives)}`, 12, 22);
  }
}

/** 2 — Chord Tower */
class ChordTower extends BaseGame {
  readonly meta = ARCADE_CATALOG[1]!;
  private chord: number[] = [];
  private held = new Set<number>();
  private timer = 4;
  private limit = 4;
  private floors = 0;

  onStart(): void {
    this.floors = 0;
    this.limit = 4;
    this.nextChord();
  }

  private nextChord(): void {
    this.chord = WHITE_TRIADS[Math.floor(Math.random() * WHITE_TRIADS.length)]!.slice();
    this.held.clear();
    this.timer = this.limit;
    this.limit = Math.max(2.2, this.limit - 0.1);
    this.hooks.setHud(this.score, `Floor ${this.floors + 1}`);
  }

  tick(dt: number): void {
    if (!this.alive) return;
    this.timer -= dt;
    if (this.timer <= 0) this.gameOver("Tower collapsed — chord timeout");
  }

  onNote(midi: number, _v: number, _s: string): void {
    if (!this.alive) return;
    const idx = findMatchIndex(this.chord, midi);
    if (idx >= 0) {
      this.held.add(this.chord[idx]!);
      sfx.play("hit");
      if (this.chord.every((n) => this.held.has(n))) {
        this.floors += 1;
        this.bump(250 + this.floors * 40);
        sfx.play("coin");
        this.nextChord();
      }
    } else {
      sfx.play("miss");
      this.timer -= 0.35;
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = "#07141c";
    ctx.fillRect(0, 0, this.w, this.h);
    const floorsH = Math.min(this.floors, 12) * 14;
    ctx.fillStyle = "#178a64";
    ctx.fillRect(this.w * 0.35, this.h - 30 - floorsH, this.w * 0.3, floorsH);
    ctx.fillStyle = "#f0c15a";
    ctx.fillRect(this.w * 0.33, this.h - 30, this.w * 0.34, 12);
    ctx.fillStyle = "#e8f7f1";
    ctx.font = "700 22px Oxanium, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(this.chord.map(NOTE_NAME).join("  ·  "), this.w / 2, 56);
    ctx.font = "12px Oxanium, sans-serif";
    ctx.fillStyle = "#8bbbab";
    ctx.fillText("Play all notes (any order)", this.w / 2, 82);
    const pct = Math.max(0, this.timer / this.limit);
    ctx.fillStyle = pct < 0.3 ? "#e85d5d" : "#2fe6b0";
    ctx.fillRect(40, this.h - 18, (this.w - 80) * pct, 8);
  }
}

/** 3 — Scale Racer (timed note presses, shrinking window) */
class ScaleRacer extends BaseGame {
  readonly meta = ARCADE_CATALOG[2]!;
  private idx = 0;
  private ascending = true;
  private dist = 0;
  private hp = 3;
  private speed = 70;
  private window = 2.8;
  private windowMax = 2.8;
  private timer = 2.8;

  onStart(): void {
    this.idx = 0;
    this.ascending = true;
    this.dist = 0;
    this.hp = 3;
    this.speed = 70;
    this.windowMax = 2.8;
    this.window = 2.8;
    this.timer = 2.8;
  }

  private target(): number {
    return C_MAJOR[this.idx]!;
  }

  private advanceScale(): void {
    if (this.ascending) {
      this.idx += 1;
      if (this.idx >= C_MAJOR.length - 1) this.ascending = false;
    } else {
      this.idx -= 1;
      if (this.idx <= 0) this.ascending = true;
    }
    this.windowMax = Math.max(0.75, this.windowMax - 0.08);
    this.window = this.windowMax;
    this.timer = this.window;
  }

  private loseLife(why: string): void {
    this.hp -= 1;
    sfx.play("miss");
    this.speed = Math.max(40, this.speed * 0.6);
    if (this.hp <= 0) {
      this.gameOver(why);
      return;
    }
    this.timer = this.window;
    this.hooks.setHud(this.score, `HP ${this.hp} · Next ${NOTE_NAME(this.target())}`);
  }

  tick(dt: number): void {
    if (!this.alive) return;
    this.timer -= dt;
    this.dist += this.speed * dt;
    this.score = Math.floor(this.dist);
    this.hooks.setHud(this.score, `HP ${this.hp} · Next ${NOTE_NAME(this.target())}`);
    if (this.timer <= 0) this.loseLife("Too slow — note window expired");
  }

  onNote(midi: number): void {
    if (!this.alive) return;
    if (pitchMatch(midi, this.target())) {
      sfx.play("hit");
      this.speed = Math.min(340, this.speed + 16);
      this.advanceScale();
      this.hooks.setHud(this.score, `HP ${this.hp} · Next ${NOTE_NAME(this.target())}`);
    } else {
      this.loseLife("Wrong note — crashed");
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = "#0a1218";
    ctx.fillRect(0, 0, this.w, this.h);
    const roadY = this.h * 0.62;
    ctx.fillStyle = "#1a2834";
    ctx.fillRect(0, roadY, this.w, this.h - roadY);
    const scroll = (this.dist * 0.4) % 60;
    ctx.strokeStyle = "#f0c15a";
    ctx.setLineDash([12, 14]);
    ctx.lineDashOffset = -scroll;
    ctx.beginPath();
    ctx.moveTo(0, roadY + 24);
    ctx.lineTo(this.w, roadY + 24);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = "#2fe6b0";
    ctx.fillRect(this.w * 0.22, roadY - 28, 48, 22);
    ctx.fillStyle = "#e8f7f1";
    ctx.font = "700 32px Oxanium, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(NOTE_NAME(this.target()), this.w * 0.72, roadY - 44);
    ctx.font = "12px Oxanium, sans-serif";
    ctx.fillStyle = "#8bbbab";
    ctx.fillText(this.ascending ? "ASCEND" : "DESCEND", this.w * 0.72, roadY - 18);
    const pct = Math.max(0, this.timer / this.window);
    ctx.fillStyle = pct < 0.28 ? "#e85d5d" : "#2fe6b0";
    ctx.fillRect(40, this.h - 20, (this.w - 80) * pct, 10);
    ctx.fillStyle = "#f0c15a";
    ctx.font = "700 13px Oxanium, sans-serif";
    ctx.textAlign = "left";
    ctx.fillText(`LIVES ${this.hp}`, 12, 22);
  }
}

/** 4 — Flappy Note (pixel pipe collision + speed ramp) */
class FlappyNote extends BaseGame {
  readonly meta = ARCADE_CATALOG[3]!;
  private y = 0.5;
  private targetY = 0.5;
  private gates: Array<{ x: number; gap: number; scored: boolean }> = [];
  private scroll = 100;
  private grace = 2;
  private readonly ballR = 14;
  private readonly pipeW = 40;
  private readonly gapPxRatio = 0.32;

  onStart(): void {
    this.y = 0.5;
    this.targetY = 0.5;
    this.gates = [];
    this.scroll = 100;
    this.grace = 2;
    this.gates.push({ x: this.w + 220, gap: 0.5, scored: false });
  }

  private gapHalf(): number {
    return (this.h * this.gapPxRatio) / 2;
  }

  private hitsPipe(g: { x: number; gap: number }): boolean {
    const bx = this.w * 0.28;
    const by = this.y * this.h;
    const left = g.x;
    const right = g.x + this.pipeW;
    // Only collide while overlapping pipe in X
    if (bx + this.ballR < left || bx - this.ballR > right) return false;
    const mid = g.gap * this.h;
    const gapTop = mid - this.gapHalf();
    const gapBot = mid + this.gapHalf();
    // Circle vs top pipe
    if (by - this.ballR < gapTop) return true;
    // Circle vs bottom pipe
    if (by + this.ballR > gapBot) return true;
    return false;
  }

  tick(dt: number): void {
    if (!this.alive) return;
    this.grace = Math.max(0, this.grace - dt);
    this.y += (this.targetY - this.y) * Math.min(1, dt * 5.5);
    this.scroll = Math.min(300, this.scroll + dt * 5.5);
    for (const g of this.gates) g.x -= this.scroll * dt;
    this.gates = this.gates.filter((g) => g.x > -60);
    if (!this.gates.length || this.gates[this.gates.length - 1]!.x < this.w - 280) {
      this.gates.push({
        x: this.w + 90,
        gap: 0.32 + Math.random() * 0.36,
        scored: false,
      });
    }

    this.hooks.setHud(this.score, `${Math.round(this.scroll)} px/s`);
    if (this.grace > 0) return;

    const bx = this.w * 0.28;
    for (const g of this.gates) {
      if (this.hitsPipe(g)) {
        this.gameOver("Hit a pipe");
        return;
      }
      if (!g.scored && g.x + this.pipeW < bx - this.ballR) {
        g.scored = true;
        this.bump(150);
        sfx.play("coin");
      }
    }
  }

  onNote(midi: number): void {
    if (!this.alive) return;
    const lo = 60;
    const hi = 72;
    const t = Math.max(0, Math.min(1, (midi - lo) / (hi - lo)));
    this.targetY = 1 - t;
    sfx.play("hit");
  }

  draw(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = "#08141c";
    ctx.fillRect(0, 0, this.w, this.h);
    const gapH = this.h * this.gapPxRatio;
    for (const g of this.gates) {
      const mid = g.gap * this.h;
      ctx.fillStyle = "#178a64";
      ctx.fillRect(g.x, 0, this.pipeW, Math.max(4, mid - gapH / 2));
      ctx.fillRect(g.x, mid + gapH / 2, this.pipeW, Math.max(4, this.h - (mid + gapH / 2)));
      ctx.fillStyle = "#2fe6b0";
      ctx.fillRect(g.x - 2, mid - gapH / 2 - 4, this.pipeW + 4, 4);
      ctx.fillRect(g.x - 2, mid + gapH / 2, this.pipeW + 4, 4);
    }
    const bx = this.w * 0.28;
    const by = this.y * this.h;
    ctx.fillStyle = "#f0c15a";
    ctx.beginPath();
    ctx.arc(bx, by, this.ballR, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#8bbbab";
    ctx.font = "12px Oxanium, sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("Low keys = low · High keys = high · Speed ramps up", 12, 20);
  }
}

/** 5 — Rhythm Simon */
class RhythmSimon extends BaseGame {
  readonly meta = ARCADE_CATALOG[4]!;
  private pattern: number[] = [];
  private inputIdx = 0;
  private phase: "demo" | "input" | "wait" = "wait";
  private demoIdx = 0;
  private demoTimer = 0;
  private growTimer: ReturnType<typeof setTimeout> | null = null;

  onStart(): void {
    this.pattern = [];
    this.clearGrowTimer();
    this.grow();
  }

  private clearGrowTimer(): void {
    if (this.growTimer != null) {
      clearTimeout(this.growTimer);
      this.growTimer = null;
    }
  }

  private grow(): void {
    this.clearGrowTimer();
    this.hooks.silence();
    this.pattern.push(C_MAJOR[Math.floor(Math.random() * C_MAJOR.length)]!);
    this.inputIdx = 0;
    this.phase = "demo";
    this.demoIdx = 0;
    this.demoTimer = 0.45;
    this.hooks.setIgnoreApiNotes(true);
    this.hooks.setHud(this.score, `Round ${this.pattern.length}`);
  }

  tick(dt: number): void {
    if (!this.alive) return;
    if (this.phase !== "demo") return;
    this.demoTimer -= dt;
    if (this.demoTimer > 0) return;
    if (this.demoIdx < this.pattern.length) {
      const m = this.pattern[this.demoIdx]!;
      this.hooks.silence();
      this.hooks.playTone(m, 90);
      sfx.play("countdown");
      this.demoIdx += 1;
      this.demoTimer = 0.55;
    } else {
      this.hooks.silence();
      this.phase = "input";
      this.hooks.setIgnoreApiNotes(false);
      this.hooks.setHud(this.score, "YOUR TURN");
    }
  }

  onNote(midi: number, _v: number, _source: string): void {
    if (!this.alive || this.phase !== "input") return;
    const expect = this.pattern[this.inputIdx]!;
    if (pitchMatch(midi, expect)) {
      this.inputIdx += 1;
      sfx.play("hit");
      if (this.inputIdx >= this.pattern.length) {
        this.bump(200 + this.pattern.length * 50);
        sfx.play("combo");
        this.phase = "wait";
        this.hooks.silence();
        this.growTimer = setTimeout(() => {
          this.growTimer = null;
          if (this.alive) this.grow();
        }, 450);
      }
    } else {
      this.gameOver(`Memory break at note ${this.inputIdx + 1}`);
    }
  }

  onStop(): void {
    this.clearGrowTimer();
    this.hooks?.silence();
    this.hooks?.setIgnoreApiNotes(false);
  }

  draw(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = "#061018";
    ctx.fillRect(0, 0, this.w, this.h);
    const n = Math.max(this.pattern.length, 1);
    const pad = 16;
    const cell = Math.min(64, (this.w - pad * 2) / n);
    for (let i = 0; i < this.pattern.length; i++) {
      const x = pad + i * cell;
      const active =
        (this.phase === "demo" && i === this.demoIdx - 1) ||
        (this.phase === "input" && i === this.inputIdx);
      ctx.fillStyle = active ? "#2fe6b0" : i < this.inputIdx ? "#178a64" : "#1a2834";
      ctx.fillRect(x, this.h * 0.4, cell - 8, 48);
      ctx.fillStyle = "#e8f7f1";
      ctx.font = "700 12px Oxanium, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(NOTE_NAME(this.pattern[i]!), x + (cell - 8) / 2, this.h * 0.4 + 28);
    }
    ctx.fillStyle = "#f0c15a";
    ctx.font = "700 16px Oxanium, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(this.phase === "demo" ? "WATCH" : this.phase === "input" ? "REPEAT" : "…", this.w / 2, 36);
  }
}

/** 6 — Tempo Runner: each gate demands a specific note in the hit window */
class TempoRunner extends BaseGame {
  readonly meta = ARCADE_CATALOG[5]!;
  private gates: Array<{ x: number; midi: number; cleared: boolean; failed: boolean }> = [];
  private scroll = 140;
  private grace = 1.6;
  private readonly hitX = 0.28;
  private readonly windowW = 70;

  onStart(): void {
    this.gates = [];
    this.scroll = 140;
    this.grace = 1.6;
    this.spawnGate(this.w + 160);
  }

  private spawnGate(x: number): void {
    const midi = C_MAJOR[Math.floor(Math.random() * C_MAJOR.length)]!;
    this.gates.push({ x, midi, cleared: false, failed: false });
  }

  tick(dt: number): void {
    if (!this.alive) return;
    this.grace = Math.max(0, this.grace - dt);
    this.scroll = Math.min(320, this.scroll + dt * 4);
    for (const g of this.gates) g.x -= this.scroll * dt;
    this.gates = this.gates.filter((g) => g.x > -50);
    if (!this.gates.length || this.gates[this.gates.length - 1]!.x < this.w - 300) {
      this.spawnGate(this.w + 40 + Math.random() * 60);
    }

    this.hooks.setHud(this.score, `${Math.round(this.scroll)} spd`);
    if (this.grace > 0) return;

    const px = this.w * this.hitX;
    for (const g of this.gates) {
      // Passed the hit window without clearing → crash
      if (!g.cleared && !g.failed && g.x + 28 < px - this.windowW * 0.35) {
        this.gameOver(`Missed ${NOTE_NAME(g.midi)} gate`);
        return;
      }
    }
  }

  onNote(midi: number): void {
    if (!this.alive || this.grace > 0) return;
    const px = this.w * this.hitX;
    // Prefer gate currently in the hit window
    const inWindow = this.gates.find(
      (g) =>
        !g.cleared &&
        !g.failed &&
        g.x < px + this.windowW &&
        g.x > px - this.windowW,
    );
    const target = inWindow ?? this.gates.find((g) => !g.cleared && !g.failed);
    if (!target) return;

    if (inWindow && pitchMatch(midi, inWindow.midi)) {
      inWindow.cleared = true;
      this.bump(140 + Math.floor(this.scroll * 0.4));
      sfx.play("hit");
      sfx.play("coin");
      return;
    }

    // Wrong note while a gate is in window = crash
    if (inWindow) {
      this.gameOver(`Wrong note — needed ${NOTE_NAME(inWindow.midi)}`);
      return;
    }

    // Early press before window — soft miss feedback only
    if (target && pitchMatch(midi, target.midi)) {
      sfx.play("countdown");
    } else {
      sfx.play("miss");
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = "#081018";
    ctx.fillRect(0, 0, this.w, this.h);
    const ground = this.h * 0.72;
    ctx.fillStyle = "#1a2834";
    ctx.fillRect(0, ground, this.w, this.h - ground);

    // Hit window
    const px = this.w * this.hitX;
    ctx.fillStyle = "rgba(240,193,90,0.12)";
    ctx.fillRect(px - this.windowW, 0, this.windowW * 2, this.h);
    ctx.strokeStyle = "rgba(240,193,90,0.55)";
    ctx.setLineDash([6, 6]);
    ctx.strokeRect(px - this.windowW, 8, this.windowW * 2, this.h - 16);
    ctx.setLineDash([]);

    for (const g of this.gates) {
      const inHit = g.x < px + this.windowW && g.x > px - this.windowW;
      ctx.fillStyle = g.cleared ? "#178a64" : inHit ? "#f0c15a" : "#e85d5d";
      ctx.fillRect(g.x, ground - 56, 36, 56);
      ctx.fillStyle = "#041018";
      ctx.font = "700 14px Oxanium, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(NOTE_NAME(g.midi), g.x + 18, ground - 28);
    }

    ctx.fillStyle = "#2fe6b0";
    ctx.fillRect(px - 14, ground - 30, 28, 28);
    ctx.fillStyle = "#8bbbab";
    ctx.font = "12px Oxanium, sans-serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
    ctx.fillText("Play the gate's note inside the gold window", 12, 22);
  }
}

/** 7 — Dual Hands (both-hand simultaneous targets) */
class DualHands extends BaseGame {
  readonly meta = ARCADE_CATALOG[6]!;
  private lh = 48;
  private rh = 64;
  private held = new Set<number>();
  private timer = 3.2;
  private limit = 3.2;
  private lives = 3;
  private round = 0;

  private readonly LH_POOL = [48, 50, 52, 53, 55]; // C3–G3 area
  private readonly RH_POOL = [60, 62, 64, 65, 67, 69, 71, 72];

  onStart(): void {
    this.lives = 3;
    this.round = 0;
    this.limit = 3.2;
    this.nextPair();
  }

  private nextPair(): void {
    this.round += 1;
    this.lh = this.LH_POOL[Math.floor(Math.random() * this.LH_POOL.length)]!;
    this.rh = this.RH_POOL[Math.floor(Math.random() * this.RH_POOL.length)]!;
    this.held.clear();
    this.limit = Math.max(1.35, 3.2 - this.round * 0.08);
    this.timer = this.limit;
    this.hooks.setHud(this.score, `Round ${this.round} · HP ${this.lives}`);
  }

  private lose(why: string): void {
    this.lives -= 1;
    sfx.play("miss");
    if (this.lives <= 0) {
      this.gameOver(why);
      return;
    }
    this.nextPair();
  }

  tick(dt: number): void {
    if (!this.alive) return;
    this.timer -= dt;
    if (this.timer <= 0) this.lose("Too slow — both hands must land");
  }

  onNote(midi: number): void {
    if (!this.alive) return;
    if (pitchMatch(midi, this.lh) || pitchMatch(midi, this.rh)) {
      if (pitchMatch(midi, this.lh)) this.held.add(this.lh);
      if (pitchMatch(midi, this.rh)) this.held.add(this.rh);
      sfx.play("hit");
      if (this.held.has(this.lh) && this.held.has(this.rh)) {
        this.bump(180 + this.round * 15);
        sfx.play("coin");
        this.nextPair();
      }
    } else {
      this.lose(`Wrong pitch — need ${NOTE_NAME(this.lh)} + ${NOTE_NAME(this.rh)}`);
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = "#061018";
    ctx.fillRect(0, 0, this.w, this.h);
    // Split stage
    ctx.fillStyle = "rgba(47,230,176,0.08)";
    ctx.fillRect(0, 0, this.w / 2 - 2, this.h);
    ctx.fillStyle = "rgba(240,193,90,0.08)";
    ctx.fillRect(this.w / 2 + 2, 0, this.w / 2 - 2, this.h);
    ctx.fillStyle = "#2fe6b0";
    ctx.font = "700 14px Oxanium, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("LEFT HAND", this.w * 0.25, 36);
    ctx.fillStyle = "#f0c15a";
    ctx.fillText("RIGHT HAND", this.w * 0.75, 36);
    ctx.fillStyle = this.held.has(this.lh) ? "#3dff9a" : "#e8f7f1";
    ctx.font = "700 42px Oxanium, sans-serif";
    ctx.fillText(NOTE_NAME(this.lh), this.w * 0.25, this.h * 0.48);
    ctx.fillStyle = this.held.has(this.rh) ? "#3dff9a" : "#e8f7f1";
    ctx.fillText(NOTE_NAME(this.rh), this.w * 0.75, this.h * 0.48);
    ctx.fillStyle = "#8bbbab";
    ctx.font = "12px Oxanium, sans-serif";
    ctx.fillText("Play BOTH notes (any order) before the bar empties", this.w / 2, this.h * 0.72);
    const pct = Math.max(0, this.timer / this.limit);
    ctx.fillStyle = pct < 0.28 ? "#e85d5d" : "#2fe6b0";
    ctx.fillRect(40, this.h - 22, (this.w - 80) * pct, 10);
    ctx.fillStyle = "#f0c15a";
    ctx.font = "700 13px Oxanium, sans-serif";
    ctx.textAlign = "left";
    ctx.fillText(`LIVES ${this.lives}`, 12, 22);
  }
}

export function createArcadeGame(id: ArcadeGameId): ArcadeGame {
  switch (id) {
    case "note-invaders":
      return new NoteInvaders();
    case "chord-tower":
      return new ChordTower();
    case "scale-racer":
      return new ScaleRacer();
    case "flappy-note":
      return new FlappyNote();
    case "rhythm-simon":
      return new RhythmSimon();
    case "tempo-runner":
      return new TempoRunner();
    case "dual-hands":
      return new DualHands();
  }
}
